import type {
  ApiErrorBody,
  AppointmentView,
  AvailabilityView,
  BotProfileView,
  ClientProfileView,
  ServiceView,
} from '@masterzapis/types';
import { webApp } from './telegram';

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

/**
 * Ошибка с кодом от сервера.
 *
 * Код нужен, чтобы отличать «слот только что заняли» — обычное течение
 * событий, после которого надо обновить сетку, — от настоящих сбоев.
 */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set('Content-Type', 'application/json');

  if (webApp?.initData) {
    headers.set('X-Telegram-Init-Data', webApp.initData);
  } else if (import.meta.env.VITE_DEV_TELEGRAM_USER) {
    // Вне Telegram подписи нет. Бэкенд примет этот заголовок только при
    // включённом DEV_AUTH_ENABLED и никогда в продакшене
    // Заголовки не переносят кириллицу как есть — имя кодируется процентами
    headers.set(
      'X-Dev-Telegram-User',
      encodeURIComponent(import.meta.env.VITE_DEV_TELEGRAM_USER),
    );
  }

  const response = await fetch(`${BASE}${path}`, { ...init, headers });

  if (response.status === 204) {
    return undefined as T;
  }

  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const error = body as ApiErrorBody | null;
    throw new ApiError(
      error?.code ?? 'INTERNAL_ERROR',
      error?.message ?? 'Не удалось связаться с сервером',
      response.status,
    );
  }

  return body as T;
}

export const api = {
  getProfile: (botId: string) => request<BotProfileView>(`/bots/${botId}/profile`),

  getServices: (botId: string) => request<ServiceView[]>(`/bots/${botId}/services`),

  getAvailability: (botId: string, serviceId: string, days = 21) =>
    request<AvailabilityView>(
      `/bots/${botId}/availability?serviceId=${serviceId}&days=${days}`,
    ),

  getMe: (botId: string) => request<ClientProfileView>(`/bots/${botId}/me`),

  getAppointments: (botId: string) => request<AppointmentView[]>(`/bots/${botId}/appointments`),

  createAppointment: (
    botId: string,
    payload: { serviceId: string; startsAt: string; comment?: string; phone?: string },
  ) =>
    request<AppointmentView>(`/bots/${botId}/appointments`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  cancelAppointment: (botId: string, appointmentId: string, reason?: string) =>
    request<AppointmentView>(`/bots/${botId}/appointments/${appointmentId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  rescheduleAppointment: (botId: string, appointmentId: string, startsAt: string) =>
    request<AppointmentView>(`/bots/${botId}/appointments/${appointmentId}/reschedule`, {
      method: 'POST',
      body: JSON.stringify({ startsAt }),
    }),
};
